import { useState, useMemo, useEffect } from "react";
import { Filter, Search } from "lucide-react";
import DatePicker from "react-datepicker";
import { format } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";
import DashboardLayout from "../../../components/dashboard-layout/DashboardLayout";
import styles from "../../../styles/__labOrderManagement.module.scss";
import ServiceNameTooltip from "../../../components/service-name-tooltip/ServiceNameTooltip";
import logo from "../../../assets/images/logo.png";
import user from "../../../assets/images/user.png";
import { IoMdSettings } from "react-icons/io";
import { PiChatCircleTextLight } from "react-icons/pi";
import DialogReschedule from "../../../components/dialog-reschedule/DialogReschedule";
import DialogCreateFollowUp from "../../../components/dialog-create/DialogCreateFollowUp";
import DialogManageAvailability from "../../../components/dialog-manage-availability/DialogManageAvailability";
import DialogPatientRegistration from "../../../components/patient-registration/DialogPatientRegistration";
import Chat from "../../../components/chat/ChatModal";
import ChatIconWithBadge from "../../../components/chat/ChatIconWithBadge";
import useChatUnread from "../../../hooks/useChatUnread";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { joinDoctorAppointmentReducer } from "../../../reducer/joinDoctorAppointment/joinDoctorAppointmentReducer";
import AppointmentDetails from "../../../components/appointmentDetails/AppointmentDetails";
import Loader from "../../../components/loader/Loader";
import LabDashboardLayout from "../../../components/labmod-dashboard-layout/LabModDashboardLayout";
import { FiDownload, FiPrinter } from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";
import { fetchImagingOrders } from "../../../slice/imagingOrders/imagingOrdersSlice";
import ImagingInvoiceModal from "../../../components/imaging-invoice-modal/ImagingInvoiceModal";
import SlaTimer from "../../../components/sla-timer/SlaTimer";
import {
  BookingPatientNameCell,
  BookingChildNameCell,
} from "../../../components/booking-patient-cells/BookingPatientCells";
import { composeBookingChildName } from "../../../utils/childBooking";
import { toast } from "../../../components/toast";
import { APP_PATH } from "../../../api/backendPath";
import { formatDateNumeric as tzDateNumeric } from "../../../utils/datetime";
import { formatWholeAmount } from "../../../utils/amountFormat";

const ImagingOrderManagement = ({ onLogout, onNavigateToDashboard }) => {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);
  const [activeTab, setActiveTab] = useState("Received");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [dateFilter, setDateFilter] = useState("");
  // Location filter (replaces the old status dropdown): "", HOME_VISIT, ON_SITE
  const [locationFilter, setLocationFilter] = useState("");
  // Search typeahead dropdown state
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const navigate = useNavigate();
  const itemsPerPage = 10;
  const [date, setDate] = useState(null);
  const [openDialogReschedule, setOpenDialogReschedule] = useState(false);
  const [openDialogCreateFollowUp, setOpenDialogCreateFollowUp] =
    useState(false);
  const [openDialogManageAvailability, setOpenDialogManageAvailability] =
    useState(false);
  const [openDialogPatientRegistration, setOpenDialogPatientRegistration] =
    useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatTarget, setChatTarget] = useState(null);
  const {
    unreadForAppointment,
    refresh: refreshChatUnread,
    clearAppointment,
  } = useChatUnread();

  const openChat = (item) => {
    setChatTarget(item);
    setIsChatOpen(true);
    clearAppointment(item?.bookingId ?? item?.booking_id ?? item?.id);
  };

  const closeChat = () => {
    setIsChatOpen(false);
    setChatTarget(null);
    refreshChatUnread();
  };

  // Export the current (filtered) imaging orders to CSV — mirrors the lab app.
  const handleExport = async () => {
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      if (!token) {
        toast.error("Authentication required. Please login again.");
        return;
      }

      const params = new URLSearchParams();
      if (activeTab && TAB_STATUS_MAP[activeTab]) {
        params.append("status", TAB_STATUS_MAP[activeTab]);
      }
      if (dateFilter) {
        params.append("date", dateFilter);
      }
      if (searchTerm) {
        params.append("search", searchTerm);
      }

      const baseURL = process.env.REACT_APP_API_URL || "";
      const response = await fetch(
        `${baseURL}${APP_PATH.exportImagingOrders}?${params.toString()}`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("text/csv")) {
        const csvContent = await response.text();
        const blob = new Blob([csvContent], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "imaging-orders.csv";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success("Data exported successfully!");
      } else {
        const result = await response.json();
        if (result.status && result.data) {
          const blob = new Blob([result.data.csv || result.data], {
            type: "text/csv",
          });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = result.data.filename || "imaging-orders.csv";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          toast.success("Data exported successfully!");
        } else {
          throw new Error(result.message || "Export failed");
        }
      }
    } catch (error) {
      console.error("Export error:", error);
      toast.error(`Failed to export data: ${error.message}`);
    }
  };

  // ── Invoice (Print) modal — same design as the imaging appointment-detail /
  //    pharmacy invoice. Print icon shows only on accepted/scheduled/dispatched/
  //    in-progress/completed orders; clicking fetches the full order details
  //    (pricing + insurance + patient) and opens the printable invoice.
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceOrder, setInvoiceOrder] = useState(null);
  const [centerProfile, setCenterProfile] = useState(null);
  const PRINTABLE_STATUSES = [
    "ORDER_ACCEPTED",
    "TECHNICIAN_ASSIGNED",
    "TECHNICIAN_DISPATCHED",
    "IN_PROGRESS",
    "COMPLETED",
  ];

  const openInvoice = async (item) => {
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      const baseURL = process.env.REACT_APP_API_URL || "";
      const res = await fetch(
        `${baseURL}${APP_PATH.getImagingOrders}/${item.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const json = await res.json();
      if (json?.data) {
        setInvoiceOrder(json.data);
        setShowInvoiceModal(true);
        // Load the center profile once for the invoice header (name + logo).
        if (!centerProfile) {
          fetch(`${baseURL}/api/v1/imaging/profile`, {
            headers: { Authorization: `Bearer ${token}` },
          })
            .then((r) => r.json())
            .then((pj) => {
              if (pj?.data) setCenterProfile(pj.data);
            })
            .catch(() => {});
        }
      } else {
        toast.error(json?.message || "Failed to load invoice");
      }
    } catch (e) {
      toast.error("Failed to load invoice");
    }
  };

  const dispatch = useDispatch();

  const {
    data: imagingOrders,
    isLoading: loading,
    error,
    totalPages: reduxTotalPages,
  } = useSelector((state) => state.imagingOrders);

  const totalPages = reduxTotalPages || 1;
  const today = new Date();
  const [selectedAppointment, setSelectedAppointment] = useState(null);

  const TAB_STATUS_MAP = {
    Received: "REQUEST_SUBMITTED",
    "Awaiting Confirmation": "AWAITING_CONFIRMATION",
    "Awaiting Payment": "AWAITING_PAYMENT",
    "Order Accepted": "ORDER_ACCEPTED",
    Scheduled: "TECHNICIAN_ASSIGNED",
    Dispatched: "TECHNICIAN_DISPATCHED",
    "In-Progress": "IN_PROGRESS",
    Completed: "COMPLETED",
    // Pseudo-status recognised by `getImagingOrders` on the backend —
    // filters `b.status = COMPLETED AND b.payment_status = 'COMPLETED'`,
    // covering both "report awaiting upload" and "report already sent".
    "Results / Pending Results": "RESULTS_PENDING_OR_SENT",
    Rejected: "REJECTED",
  };

  const getPatientName = (order) => {
    const p = order?.patient;
    if (p)
      return (
        `${p.full_name || ""} ${p.surname || ""}`.trim() ||
        `Patient ${order.patientId}`
      );
    return order?.name || `Patient ${order?.patientId || ""}`;
  };

  // Patient Name + Child Name cells. Same split the doctor app makes for a child consultation:
  // on a child booking Patient Name shows a muted "No Patient" and the child (icon + name) gets
  // its own column; a self booking shows the account holder and a muted "-". Every table renders
  // both, right after each other, so the two columns line up across all tabs.
  const renderPatientCell = (item) => (
    <td>
      <BookingPatientNameCell
        childName={composeBookingChildName(item)}
        name={getPatientName(item)}
      />
    </td>
  );

  const renderChildCell = (item) => (
    <td>
      <BookingChildNameCell childName={composeBookingChildName(item)} />
    </td>
  );

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const formatDate = (dateObj) => {
    if (!dateObj) return null;
    try {
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, "0");
      const day = String(dateObj.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return null;
    }
  };

  // Format status text for display
  const formatStatusText = (status) => {
    if (!status) return "";
    // Convert to display format
    const statusDisplayMap = {
      pending: "Pending",
      upcoming: "Upcoming",
      ongoing: "Ongoing",
      completed: "Completed",
      cancelled: "Cancelled",
      reschedule_requested: "Reschedule Requested",
      "reschedule-requested": "Reschedule Requested",
    };

    const lowerStatus = status.toLowerCase();
    if (statusDisplayMap[lowerStatus]) {
      return statusDisplayMap[lowerStatus];
    }

    // Capitalize first letter of each word for other statuses
    return status
      .split(/[_\s-]+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  // Normalize status value for API (convert display format to API format)
  const normalizeStatusForAPI = (status) => {
    if (!status || status === "All") return null;
    // Convert display format to API format
    const statusMap = {
      Pending: "pending",
      Upcoming: "upcoming",
      Ongoing: "ongoing",
      Completed: "completed",
      Cancelled: "cancelled",
      "Reschedule Requested": "reschedule_requested",
      reschedule_requested: "reschedule_requested",
      pending: "pending",
      upcoming: "upcoming",
      ongoing: "ongoing",
      completed: "completed",
      cancelled: "cancelled",
    };
    return statusMap[status] || status.toLowerCase().replace(/\s+/g, "_");
  };

  const fetchOrdersWithFilters = (
    pageNum = currentPage,
    tabOverride = undefined,
    dateOverride = undefined,
    searchOverride = undefined,
    locationOverride = undefined,
  ) => {
    const tabToUse = tabOverride !== undefined ? tabOverride : activeTab;
    const dateToUse = dateOverride !== undefined ? dateOverride : date;
    const searchToUse =
      searchOverride !== undefined ? searchOverride : searchTerm;
    const locationToUse =
      locationOverride !== undefined ? locationOverride : locationFilter;
    const formattedDate = formatDate(dateToUse);
    const status = TAB_STATUS_MAP[tabToUse];
    const params = { page: pageNum, limit: itemsPerPage };
    if (status) params.status = status;
    if (formattedDate) params.date = formattedDate;
    if (searchToUse && searchToUse.trim()) params.search = searchToUse.trim();
    if (locationToUse) params.location = locationToUse;
    dispatch(fetchImagingOrders(params));
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    fetchOrdersWithFilters(page);
  };

  const resetFilters = () => {
    setSearchTerm("");
    setStatusFilter("All");
    setLocationFilter("");
    setDate(null);
    setSearchResults([]);
    setShowSearchDropdown(false);
    setCurrentPage(1);
    setActiveTab("Received");
    fetchOrdersWithFilters(1, "Received", null, "", "");
  };

  // Debounced search typeahead — matches booking number OR patient name across
  // all statuses; results shown in a dropdown, clicking navigates to details.
  useEffect(() => {
    const term = searchTerm.trim();
    if (!term) {
      setSearchResults([]);
      setShowSearchDropdown(false);
      setSearchLoading(false);
      return;
    }
    const handler = setTimeout(async () => {
      try {
        setSearchLoading(true);
        const token = sessionStorage.getItem("usr_1a2b3c");
        const baseURL = process.env.REACT_APP_API_URL || "";
        const res = await fetch(
          `${baseURL}${APP_PATH.getImagingOrders}?search=${encodeURIComponent(term)}&limit=10`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const json = await res.json();
        setSearchResults(json?.data?.orders || []);
        setShowSearchDropdown(true);
      } catch (e) {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const handleNavigate = (page) => {
    if (page === "dashboard") {
      onNavigateToDashboard();
    }
  };

  // Initial load
  useEffect(() => {
    fetchOrdersWithFilters(1, "Received");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Silent refresh when a real-time notification arrives
  useEffect(() => {
    const handleNotification = () => {
      fetchOrdersWithFilters(currentPage, activeTab, date, searchTerm);
    };
    window.addEventListener(
      "imaging_notification_received",
      handleNotification,
    );
    return () =>
      window.removeEventListener(
        "imaging_notification_received",
        handleNotification,
      );
  }, [currentPage, activeTab, date, searchTerm]);

  const handlePageChange1 = (data) => {
    const nextPage = data + 1;
    fetchOrdersWithFilters(nextPage);
  };

  const handlePageChange11 = (data) => {
    const prevPage = data - 1;
    if (prevPage >= 1) fetchOrdersWithFilters(prevPage);
  };
  const handleBackDetail = (state, data) => {
    setShowDetails(state);
    setSelectedAppointment(data);
  };

  const handleReschedules = (data) => {
    setOpenDialogReschedule(true);
    setSelectedAppointment(data);
  };

  const CreateFollowUp = (data) => {
    setOpenDialogCreateFollowUp(true);
    setSelectedAppointment(data);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Upcoming":
        return "status-upcoming";
      case "Completed":
        return "status-completed";
      case "Cancelled":
        return "status-cancelled";
      case "Reschedule Requested":
        return "status-reschedule";
      case "Pending":
        return "status-pending"; // Peach — payment "Pending" badge (matches lab)
      case "COD":
        return "status-cod";
      case "Payment Complete":
        return "status-payment";
      case "Collected":
        return "status-payment";
      case "Lab":
        return "status-labtype";
      case "Home Visit":
        return "status-labtype";
      case "Imaging":
        return "status-imagingtype";
      case "On-Site":
      case "VISIT_CENTER":
      case "Visit Center":
        return "status-onsite";
      case "Urgent":
        return "status-red";
      case "Technician Dispatched":
        return "status-dispatched";
      case "Sent":
        return "status-dispatched";
      case "COMPLETED":
      case "Completed":
        return "status-payment-completed";
      case "PENDING":
      case "Pending":
        return "status-payment-pending";
      default:
        return "";
    }
  };

  const formatPaymentStatus = (status) => {
    if (!status) return "-";
    if (status === "COMPLETED") return "Completed";
    if (status === "PENDING") return "Pending";
    return status;
  };

  const formatReportStatus = (status) => {
    if (!status || status === "NOT_SENT") return "Not Sent";
    if (status === "SENT") return "Sent";
    return status;
  };

  const getReportStatusColor = (status) => {
    if (!status || status === "NOT_SENT") return "reportstatus-NotSent";
    if (status === "SENT") return "reportstatus-Sent";
    return "";
  };

  const formatLocation = (loc) => {
    if (!loc) return "-";
    if (loc === "VISIT_CENTER" || loc === "Visit Center") return "On-Site";
    return loc;
  };

  // Format a stored time ("10:00:00"/"10:00") into a 12-hour label. Passes
  // through values that already contain AM/PM.
  const formatTime12h = (time) => {
    if (!time || typeof time !== "string") return "-";
    if (time.includes("AM") || time.includes("PM")) return time;
    const [h, m] = time.split(":").map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return "-";
    const period = h >= 12 ? "PM" : "AM";
    const displayHour = h % 12 || 12;
    return `${displayHour}:${String(m).padStart(2, "0")} ${period}`;
  };

  const orderManagementTabs = [
    "Received",
    // "Awaiting Confirmation" tab hidden (split/confirmation flow retired). Status
    // mapping + render block are kept for backward compatibility with existing orders.
    "Awaiting Payment",
    "Order Accepted",
    "Scheduled",
    "Dispatched",
    "In-Progress",
    "Completed",
    // Consolidated "Results / Pending Results" tab (right after Completed) —
    // surfaces every fully-paid completed booking regardless of whether the
    // report has been uploaded / sent, so officers can see both orders
    // awaiting a report and orders whose report has gone out. Backend
    // pseudo-status `RESULTS_PENDING_OR_SENT` filters
    // `b.status = COMPLETED AND b.payment_status = 'COMPLETED'` in
    // `getImagingOrders`.
    "Results / Pending Results",
    "Rejected",
  ];

  const awaitingPatientConfirmationTableData = [
    {
      id: "LT-00001",
      name: "Mohamed Hassan",
      requestDate: "20-12-2025",
      testName: "Vitamin D, Complete Blood Count (CBC)",
      testType: "Lab",
      preferredDate: "20-12-2025",
      preferredTimeSlot: "Morning (8:00 AM)",
      urgency: null,
      preferredGender: "Male",
      preferredLocation: "Home Visit",
    },
    {
      id: "LT-00001",
      name: "Lyla Khalid",
      requestDate: "20-12-2025",
      testName: "Vitamin D, Complete Blood Count (CBC)",
      testType: "Lab",
      preferredDate: "20-12-2025",
      preferredTimeSlot: "Morning (8:00 AM)",
      urgency: null,
      preferredGender: null,
      preferredLocation: "On-Site",
    },
    {
      id: "LT-00001",
      name: "Kamla Khalid",
      requestDate: "20-12-2025",
      testName: "Vitamin D, Complete Blood Count (CBC)",
      testType: "Lab",
      preferredDate: "20-12-2025",
      preferredTimeSlot: "Morning (8:00 AM)",
      urgency: null,
      preferredGender: "Male",
      preferredLocation: "Home Visit",
    },
    {
      id: "LT-00001",
      name: "Ruby",
      requestDate: "20-12-2025",
      testName: "Vitamin D, Complete Blood Count (CBC)",
      testType: "Lab",
      preferredDate: "20-12-2025",
      preferredTimeSlot: "Morning (8:00 AM)",
      urgency: null,
      preferredGender: null,
      preferredLocation: "On-Site",
    },
    {
      id: "LT-00001",
      name: "Kyle",
      requestDate: "20-02-2025",
      testName: "Vitamin D, Complete Blood Count (CBC)",
      testType: "Lab",
      preferredDate: "20-02-2025",
      preferredTimeSlot: "Morning (8:00 AM)",
      urgency: "Urgent",
      preferredGender: null,
      preferredLocation: "On-Site",
    },
    {
      id: "LT-00001",
      name: "Olivia",
      requestDate: "20-02-2025",
      testName: "Vitamin D, Complete Blood Count (CBC)",
      testType: "Lab",
      preferredDate: "20-02-2025",
      preferredTimeSlot: "Morning (8:00 AM)",
      urgency: null,
      preferredGender: "Male",
      preferredLocation: "Home Visit",
    },
    {
      id: "LT-00001",
      name: "Ruddy",
      requestDate: "20-02-2025",
      testName: "Vitamin D, Complete Blood Count (CBC)",
      testType: "Lab",
      preferredDate: "20-02-2025",
      preferredTimeSlot: "Morning (8:00 AM)",
      urgency: null,
      preferredGender: "Male",
      preferredLocation: "Home Visit",
    },
    {
      id: "LT-00001",
      name: "Kevin",
      requestDate: "20-02-2025",
      testName: "Vitamin D, Complete Blood Count (CBC)",
      testType: "Lab",
      preferredDate: "20-02-2025",
      preferredTimeSlot: "Morning (8:00 AM)",
      urgency: "Urgent",
      preferredGender: null,
      preferredLocation: "On-Site",
    },
    {
      id: "LT-00001",
      name: "Rishabh",
      requestDate: "20-02-2025",
      testName: "Vitamin D, Complete Blood Count (CBC)",
      testType: "Lab",
      preferredDate: "20-02-2025",
      preferredTimeSlot: "Morning (8:00 AM)",
      urgency: "Urgent",
      preferredGender: null,
      preferredLocation: "On-Site",
    },
    {
      id: "LT-00001",
      name: "Rohan",
      requestDate: "20-02-2025",
      testName: "Vitamin D, Complete Blood Count (CBC)",
      testType: "Lab",
      preferredDate: "20-02-2025",
      preferredTimeSlot: "Morning (8:00 AM)",
      urgency: "Urgent",
      preferredGender: null,
      preferredLocation: "On-Site",
    },
  ];

  const generateInvoiceTableData = [
    {
      id: "LT-00001",
      name: "Mohamed Hassan",
      testName: "Vitamin D, Complete Blood Count (CBC)",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Lab",
      urgency: null,
      location: "Home Visit",
    },
    {
      id: "LT-00001",
      name: "Lyla Khalid",
      testName: "Urine Test",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Lab",
      urgency: null,
      location: "On-Site",
    },
    {
      id: "LT-00001",
      name: "Kamla Khalid",
      testName: "Urine Test",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Lab",
      urgency: null,
      location: "Home Visit",
    },
    {
      id: "LT-00001",
      name: "Ruby",
      testName: "CBC",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Lab",
      urgency: null,
      location: "On-Site",
    },
    {
      id: "LT-00001",
      name: "Kyle",
      testName: "MRI Brain",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Imaging",
      urgency: "Urgent",
      location: "On-Site",
    },
    {
      id: "LT-00001",
      name: "Olivia",
      testName: "CBC",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Lab",
      urgency: null,
      location: "Home Visit",
    },
    {
      id: "LT-00001",
      name: "Ruddy",
      testName: "CBC",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Lab",
      urgency: null,
      location: "Home Visit",
    },
    {
      id: "LT-00001",
      name: "Kevin",
      testName: "CT Scan",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Imaging",
      urgency: "Urgent",
      location: "On-Site",
    },
    {
      id: "LT-00001",
      name: "Rishabh",
      testName: "MRI Brain",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Imaging",
      urgency: "Urgent",
      location: "On-Site",
    },
    {
      id: "LT-00001",
      name: "Rohan",
      testName: "CT Scan",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Imaging",
      urgency: "Urgent",
      location: "On-Site",
    },
  ];

  return (
    <LabDashboardLayout
      currentPage="lab_ordermanagement"
      onNavigate={handleNavigate}
      onLogout={onLogout}
    >
      {/* Front-face service-name tooltip (position:fixed, never clipped by the
          table's horizontal scroll box). Works across all tabs. */}
      <ServiceNameTooltip styles={styles} />
      <DialogReschedule
        open={openDialogReschedule}
        onClose={() => setOpenDialogReschedule(false)}
        children={undefined}
        selectedAppointment={selectedAppointment}
      />
      <DialogCreateFollowUp
        open={openDialogCreateFollowUp}
        onClose={() => {
          setOpenDialogCreateFollowUp(false);
        }}
        children={undefined}
        selectedAppointment={selectedAppointment}
      />
      <DialogManageAvailability
        open={openDialogManageAvailability}
        onClose={() => {
          setOpenDialogManageAvailability(false);
        }}
        children={undefined}
      />
      <DialogPatientRegistration
        open={openDialogPatientRegistration}
        onClose={() => {
          setOpenDialogPatientRegistration(false);
        }}
        children={undefined}
      />

      {showDetails ? (
        <AppointmentDetails
          handleBack={() => setShowDetails(false)}
          homeVisit={false}
          selectedAppointment={selectedAppointment}
        />
      ) : (
        <>
          <div className={styles["apt-page-header"]}>
            <h1>Order Management</h1>
          </div>
          <div className={styles["apt-appointments-section"]}>
            <div className={styles["apt-section-header"]}>
              <h2>{t("appointments")}</h2>
              <div className={styles["apt-top-actions"]}>
                <button className={styles["exportBtn"]} onClick={handleExport}>
                  <FiDownload /> Export
                </button>
              </div>
            </div>

            {/* Filters */}
            <div className={styles["apt-filters"]}>
              <div className={styles["apt-filter-group"]}>
                {/* <span className={styles["apt-filter-icon-wrap"]}>
                  <Filter className={styles["apt-filter-icon"]} />
                </span> */}
                <div
                  className={styles["apt-search-wrapper"]}
                  style={{ position: "relative" }}
                >
                  <Search className={styles["apt-search-icon"]} />
                  <input
                    type="text"
                    placeholder="Search appointment id or patient..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onFocus={() => {
                      if (searchResults.length > 0) setShowSearchDropdown(true);
                    }}
                    onBlur={() =>
                      setTimeout(() => setShowSearchDropdown(false), 150)
                    }
                    className={styles["apt-search-input"]}
                  />
                  {showSearchDropdown && searchTerm.trim() && (
                    <div
                      style={{
                        position: "absolute",
                        top: "calc(100% + 4px)",
                        left: 0,
                        right: 0,
                        zIndex: 50,
                        background: "#fff",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                        maxHeight: "300px",
                        overflowY: "auto",
                      }}
                    >
                      {searchLoading ? (
                        <div
                          style={{
                            padding: "12px 14px",
                            fontSize: "13px",
                            color: "#6b7280",
                          }}
                        >
                          Searching...
                        </div>
                      ) : searchResults.length === 0 ? (
                        <div
                          style={{
                            padding: "12px 14px",
                            fontSize: "13px",
                            color: "#6b7280",
                          }}
                        >
                          No orders found
                        </div>
                      ) : (
                        searchResults.map((item) => (
                          <button
                            type="button"
                            key={item.id}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setShowSearchDropdown(false);
                              navigate(
                                `/imaging_appointment_detail/${item.id}`,
                              );
                            }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                              width: "100%",
                              padding: "10px 14px",
                              background: "none",
                              border: "none",
                              borderBottom: "1px solid #f3f4f6",
                              cursor: "pointer",
                              textAlign: "left",
                            }}
                          >
                            <div style={{ minWidth: 0 }}>
                              <div
                                style={{
                                  fontSize: "13px",
                                  fontWeight: 600,
                                  color: "#111827",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {composeBookingChildName(item) ||
                                  getPatientName(item)}
                              </div>
                              <div
                                style={{ fontSize: "12px", color: "#6b7280" }}
                              >
                                {item.bookingNumber ||
                                  item.booking_number ||
                                  `#${item.id}`}
                              </div>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
                <select
                  value={locationFilter}
                  onChange={(e) => {
                    const val = e.target.value;
                    setLocationFilter(val);
                    setCurrentPage(1);
                    fetchOrdersWithFilters(
                      1,
                      undefined,
                      undefined,
                      undefined,
                      val,
                    );
                  }}
                  className={styles["apt-status-filter"]}
                  style={{ paddingRight: "30px" }}
                >
                  <option value="">All Locations</option>
                  <option value="HOME_VISIT">Home Visit</option>
                  <option value="ON_SITE">On-Site</option>
                </select>
                <div className={styles["date-wrapper"]}>
                  <DatePicker
                    key={`datepicker-${date ? date.getTime() : "empty"}`}
                    className={styles["apt-date-filter"]}
                    selected={date}
                    onChange={(d) => {
                      setDate(d);
                      setCurrentPage(1);
                      fetchOrdersWithFilters(1, undefined, d);
                    }}
                    placeholderText="Select Date"
                    isClearable
                  />
                </div>
                {/* <button className={styles["apt-apply-btn"]}>{t('appointments.filters.button.apply')}</button> */}
                {/* <button
                  className={styles["apt-apply-btn"]}
                  onClick={() => {
                    setCurrentPage(1);
                    fetchOrdersWithFilters(1);
                  }}
                >
                  {t("appointments.filters.button.apply")}
                </button> */}
                <button
                  className={styles["apt-reset-btn"]}
                  onClick={resetFilters}
                >
                  {t("appointments.filters.button.reset")}
                </button>
              </div>
            </div>

            <div className={styles["apt-appointment-tabs"]}>
              {orderManagementTabs.map((tabItem, idx) => {
                return (
                  <button
                    key={idx}
                    className={`${styles["apt-tab"]} ${
                      activeTab === tabItem ? styles["active"] : ""
                    }`}
                    onClick={() => {
                      setActiveTab(tabItem);
                      setStatusFilter(tabItem);
                      setCurrentPage(1);
                      fetchOrdersWithFilters(1, tabItem);
                    }}
                  >
                    {tabItem}
                  </button>
                );
              })}
            </div>

            {/* Table */}
            {activeTab === "Received" && (
              <div className={styles["apt-appointments-table"]}>
                <table>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>{t("childBooking.childName", "Child Name")}</th>
                      <th>Request Date</th>
                      <th>Preferred Date</th>
                      <th>Preferred Time Slot</th>
                      <th>Urgency</th>
                      {/* <th>Test Type</th> */}
                      <th>Location</th>
                      <th>
                        {t("lab.orderManagement.table.slaTime", "SLA Time")}
                      </th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={10}>
                          <Loader />
                        </td>
                      </tr>
                    ) : !(imagingOrders || []).length ? (
                      <tr>
                        <td
                          colSpan={10}
                          style={{
                            textAlign: "center",
                            padding: "20px",
                            color: "#888",
                          }}
                        >
                          No records found.
                        </td>
                      </tr>
                    ) : (
                      imagingOrders.map((item, idx) => (
                        <tr key={item.id || idx}>
                          <td>{item.bookingNumber || item.id}</td>
                          {renderPatientCell(item)}
                          {renderChildCell(item)}
                          <td>{item.requestDate || "-"}</td>
                          <td>
                            {item.preferredDate
                              ? tzDateNumeric(new Date(item.preferredDate))
                              : "-"}
                          </td>
                          <td>{item.preferredTimeSlot || "-"}</td>
                          <td>
                            <span
                              className={`${styles["db-status"]} ${
                                styles[getStatusColor(item.urgency)]
                              }`}
                            >
                              {item.urgency}
                            </span>
                          </td>
                          <td>
                            <span
                              className={`${styles["db-status"]} ${
                                styles[
                                  getStatusColor(
                                    formatLocation(item.preferredLocation),
                                  )
                                ]
                              }`}
                            >
                              {formatLocation(item.preferredLocation)}
                            </span>
                          </td>
                          <td>
                            <SlaTimer
                              dueAt={item.slaDueAt}
                              isUrgent={item.slaIsUrgent}
                              status={item.status}
                            />
                          </td>
                          <td>
                            <div className={styles["apt-action-buttons"]}>
                              <Link
                                to={`/imaging_appointment_detail/${item.id}`}
                              >
                                <button
                                  className={styles["apt-view-btn"]}
                                  title="View"
                                >
                                  <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M12 8.25C11.0054 8.25 10.0516 8.64509 9.34835 9.34835C8.64509 10.0516 8.25 11.0054 8.25 12C8.25 12.9946 8.64509 13.9484 9.34835 14.6516C10.0516 15.3549 11.0054 15.75 12 15.75C12.9946 15.75 13.9484 15.3549 14.6516 14.6516C15.3549 13.9484 15.75 12.9946 15.75 12C15.75 11.0054 15.3549 10.0516 14.6516 9.34835C13.9484 8.64509 12.9946 8.25 12 8.25ZM9.75 12C9.75 11.4033 9.98705 10.831 10.409 10.409C10.831 9.98705 11.4033 9.75 12 9.75C12.5967 9.75 13.169 9.98705 13.591 10.409C14.0129 10.831 14.25 11.4033 14.25 12C14.25 12.5967 14.0129 13.169 13.591 13.591C13.169 14.0129 12.5967 14.25 12 14.25C11.4033 14.25 10.831 14.0129 10.409 13.591C9.98705 13.169 9.75 12.5967 9.75 12Z"
                                      fill="#155DFC"
                                    />
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M12 3.25C7.486 3.25 4.445 5.954 2.68 8.247L2.649 8.288C2.249 8.807 1.882 9.284 1.633 9.848C1.366 10.453 1.25 11.112 1.25 12C1.25 12.888 1.366 13.547 1.633 14.152C1.883 14.716 2.25 15.194 2.649 15.712L2.681 15.753C4.445 18.046 7.486 20.75 12 20.75C16.514 20.75 19.555 18.046 21.32 15.753L21.351 15.712C21.751 15.194 22.118 14.716 22.367 14.152C22.634 13.547 22.75 12.888 22.75 12C22.75 11.112 22.634 10.453 22.367 9.848C22.117 9.284 21.75 8.807 21.351 8.288L21.319 8.247C19.555 5.954 16.514 3.25 12 3.25ZM3.87 9.162C5.498 7.045 8.15 4.75 12 4.75C15.85 4.75 18.501 7.045 20.13 9.162C20.57 9.732 20.826 10.072 20.995 10.454C21.153 10.812 21.25 11.249 21.25 12C21.25 12.751 21.153 13.188 20.995 13.546C20.826 13.928 20.569 14.268 20.131 14.838C18.5 16.955 15.85 19.25 12 19.25C8.15 19.25 5.499 16.955 3.87 14.838C3.43 14.268 3.174 13.928 3.005 13.546C2.847 13.188 2.75 12.751 2.75 12C2.75 11.249 2.847 10.812 3.005 10.454C3.174 10.072 3.432 9.732 3.87 9.162Z"
                                      fill="#155DFC"
                                    />
                                  </svg>
                                </button>
                              </Link>

                              {PRINTABLE_STATUSES.includes(item.status) && (
                                <button
                                  className={styles["apt-view-btn"]}
                                  title="Print Invoice"
                                  onClick={() => openInvoice(item)}
                                  style={{
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                  }}
                                >
                                  <FiPrinter size={18} color="#111827" />
                                </button>
                              )}

                              <ChatIconWithBadge
                                className={styles["apt-view-btn"]}
                                title="Chat"
                                count={unreadForAppointment(
                                  item.bookingId ?? item.booking_id ?? item.id,
                                )}
                                onClick={() => openChat(item)}
                              />
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Awaiting Confirmation Tab */}
            {activeTab === "Awaiting Confirmation" && (
              <div className={styles["apt-appointments-table"]}>
                <table>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>{t("childBooking.childName", "Child Name")}</th>
                      <th>Request Date</th>
                      <th>Test Name</th>
                      {/* <th>Test Type</th> */}
                      <th>Preferred Date</th>
                      <th>Preferred Time Slot</th>
                      <th>Urgency</th>
                      <th>Preferred Technician Gender</th>
                      <th>Preferred Location</th>
                      <th>Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={11}>
                          <Loader />
                        </td>
                      </tr>
                    ) : !(imagingOrders || []).length ? (
                      <tr>
                        <td
                          colSpan={11}
                          style={{
                            textAlign: "center",
                            padding: "20px",
                            color: "#888",
                          }}
                        >
                          No records found.
                        </td>
                      </tr>
                    ) : (
                      imagingOrders.map((item, idx) => (
                        <tr key={item.id || idx}>
                          <td>{item.bookingNumber || item.id}</td>
                          {renderPatientCell(item)}
                          {renderChildCell(item)}
                          <td>{item.requestDate || "-"}</td>
                          <td>
                            {(() => {
                              const testNames = (item.tests || [])
                                .map((t) => t.testName)
                                .filter(Boolean);
                              const display = testNames.join(", ") || "-";
                              return testNames.length > 1 ? (
                                <div className={styles["testNames"]}>
                                  <div className={styles["truncateTestNames"]}>
                                    {display}
                                  </div>
                                  <div className={styles["testNamesList"]}>
                                    <ul>
                                      <li>
                                        <span>Tests</span>
                                      </li>
                                      {testNames.map((name, i) => (
                                        <li key={i}>{name}</li>
                                      ))}
                                    </ul>
                                  </div>
                                </div>
                              ) : (
                                display
                              );
                            })()}
                          </td>

                          {/* <td>
                            <span
                              className={`${styles["db-status"]} ${
                                styles[getStatusColor(item.testType)]
                              }`}
                            >
                              {item.testType}
                            </span>
                          </td> */}

                          <td>
                            {item.preferredDate
                              ? tzDateNumeric(new Date(item.preferredDate))
                              : "-"}
                          </td>
                          <td>{item.preferredTimeSlot || "-"}</td>

                          <td>
                            <span
                              className={`${styles["db-status"]} ${
                                styles[getStatusColor(item.urgency)]
                              }`}
                            >
                              {item.urgency}
                            </span>
                          </td>

                          <td>
                            {item.preferredTechnicianGender ||
                              item.preferredGender ||
                              "-"}
                          </td>
                          <td>
                            <span
                              className={`${styles["db-status"]} ${
                                styles[
                                  getStatusColor(
                                    formatLocation(item.preferredLocation),
                                  )
                                ]
                              }`}
                            >
                              {formatLocation(item.preferredLocation)}
                            </span>
                          </td>

                          {/* ACTION COLUMN */}
                          <td>
                            <div className={styles["apt-action-buttons"]}>
                              <Link
                                to={`/imaging_appointment_detail/${item.id}`}
                              >
                                <button
                                  className={styles["apt-view-btn"]}
                                  title="View"
                                >
                                  <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M12 8.25C11.0054 8.25 10.0516 8.64509 9.34835 9.34835C8.64509 10.0516 8.25 11.0054 8.25 12C8.25 12.9946 8.64509 13.9484 9.34835 14.6516C10.0516 15.3549 11.0054 15.75 12 15.75C12.9946 15.75 13.9484 15.3549 14.6516 14.6516C15.3549 13.9484 15.75 12.9946 15.75 12C15.75 11.0054 15.3549 10.0516 14.6516 9.34835C13.9484 8.64509 12.9946 8.25 12 8.25ZM9.75 12C9.75 11.4033 9.98705 10.831 10.409 10.409C10.831 9.98705 11.4033 9.75 12 9.75C12.5967 9.75 13.169 9.98705 13.591 10.409C14.0129 10.831 14.25 11.4033 14.25 12C14.25 12.5967 14.0129 13.169 13.591 13.591C13.169 14.0129 12.5967 14.25 12 14.25C11.4033 14.25 10.831 14.0129 10.409 13.591C9.98705 13.169 9.75 12.5967 9.75 12Z"
                                      fill="#155DFC"
                                    />
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M12 3.25C7.486 3.25 4.445 5.954 2.68 8.247L2.649 8.288C2.249 8.807 1.882 9.284 1.633 9.848C1.366 10.453 1.25 11.112 1.25 12C1.25 12.888 1.366 13.547 1.633 14.152C1.883 14.716 2.25 15.194 2.649 15.712L2.681 15.753C4.445 18.046 7.486 20.75 12 20.75C16.514 20.75 19.555 18.046 21.32 15.753L21.351 15.712C21.751 15.194 22.118 14.716 22.367 14.152C22.634 13.547 22.75 12.888 22.75 12C22.75 11.112 22.634 10.453 22.367 9.848C22.117 9.284 21.75 8.807 21.351 8.288L21.319 8.247C19.555 5.954 16.514 3.25 12 3.25ZM3.87 9.162C5.498 7.045 8.15 4.75 12 4.75C15.85 4.75 18.501 7.045 20.13 9.162C20.57 9.732 20.826 10.072 20.995 10.454C21.153 10.812 21.25 11.249 21.25 12C21.25 12.751 21.153 13.188 20.995 13.546C20.826 13.928 20.569 14.268 20.131 14.838C18.5 16.955 15.85 19.25 12 19.25C8.15 19.25 5.499 16.955 3.87 14.838C3.43 14.268 3.174 13.928 3.005 13.546C2.847 13.188 2.75 12.751 2.75 12C2.75 11.249 2.847 10.812 3.005 10.454C3.174 10.072 3.432 9.732 3.87 9.162Z"
                                      fill="#155DFC"
                                    />
                                  </svg>
                                </button>
                              </Link>
                              {PRINTABLE_STATUSES.includes(item.status) && (
                                <button
                                  className={styles["apt-view-btn"]}
                                  title="Print Invoice"
                                  onClick={() => openInvoice(item)}
                                  style={{
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                  }}
                                >
                                  <FiPrinter size={18} color="#111827" />
                                </button>
                              )}

                              <ChatIconWithBadge
                                className={styles["apt-view-btn"]}
                                title="Chat"
                                count={unreadForAppointment(
                                  item.bookingId ?? item.booking_id ?? item.id,
                                )}
                                onClick={() => openChat(item)}
                              />
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Generate Invoice Tab */}
            {activeTab === "Generate Invoice" && (
              <div className={styles["apt-appointments-table"]}>
                <table>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>{t("childBooking.childName", "Child Name")}</th>
                      <th>Test Name</th>
                      <th>Service Date</th>
                      <th>Service Time</th>
                      {/* <th>Test Type</th> */}
                      <th>Urgency</th>
                      <th>Location</th>
                      <th>Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={9}>
                          <Loader />
                        </td>
                      </tr>
                    ) : (
                      generateInvoiceTableData.map((item, idx) => (
                        <tr key={item.id || idx}>
                          <td>{item.bookingNumber || item.id}</td>
                          {renderPatientCell(item)}
                          {renderChildCell(item)}
                          <td>
                            {(() => {
                              const testNames = (item.tests || [])
                                .map((t) => t.testName)
                                .filter(Boolean);
                              const display = testNames.join(", ") || "-";
                              return testNames.length > 1 ? (
                                <div className={styles["testNames"]}>
                                  <div className={styles["truncateTestNames"]}>
                                    {display}
                                  </div>
                                  <div className={styles["testNamesList"]}>
                                    <ul>
                                      <li>
                                        <span>Tests</span>
                                      </li>
                                      {testNames.map((name, i) => (
                                        <li key={i}>{name}</li>
                                      ))}
                                    </ul>
                                  </div>
                                </div>
                              ) : (
                                display
                              );
                            })()}
                          </td>
                          <td>
                            {item.scheduledDate
                              ? tzDateNumeric(new Date(item.scheduledDate))
                              : "-"}
                          </td>
                          <td>{formatTime12h(item.scheduledTime)}</td>

                          {/* <td>
                            <span
                              className={`${styles["db-status"]} ${
                                styles[getStatusColor(item.testType)]
                              }`}
                            >
                              {item.testType}
                            </span>
                          </td> */}

                          <td>
                            <span
                              className={`${styles["db-status"]} ${
                                styles[getStatusColor(item.urgency)]
                              }`}
                            >
                              {item.urgency}
                            </span>
                          </td>

                          <td>
                            <span
                              className={`${styles["db-status"]} ${
                                styles[
                                  getStatusColor(
                                    formatLocation(item.preferredLocation),
                                  )
                                ]
                              }`}
                            >
                              {formatLocation(item.preferredLocation)}
                            </span>
                          </td>
                          {/* ACTION COLUMN */}
                          <td>
                            <div className={styles["apt-action-buttons"]}>
                              <Link
                                to={`/imaging_appointment_detail/${item.id}`}
                              >
                                <button
                                  className={styles["apt-view-btn"]}
                                  title="View"
                                >
                                  <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M12 8.25C11.0054 8.25 10.0516 8.64509 9.34835 9.34835C8.64509 10.0516 8.25 11.0054 8.25 12C8.25 12.9946 8.64509 13.9484 9.34835 14.6516C10.0516 15.3549 11.0054 15.75 12 15.75C12.9946 15.75 13.9484 15.3549 14.6516 14.6516C15.3549 13.9484 15.75 12.9946 15.75 12C15.75 11.0054 15.3549 10.0516 14.6516 9.34835C13.9484 8.64509 12.9946 8.25 12 8.25ZM9.75 12C9.75 11.4033 9.98705 10.831 10.409 10.409C10.831 9.98705 11.4033 9.75 12 9.75C12.5967 9.75 13.169 9.98705 13.591 10.409C14.0129 10.831 14.25 11.4033 14.25 12C14.25 12.5967 14.0129 13.169 13.591 13.591C13.169 14.0129 12.5967 14.25 12 14.25C11.4033 14.25 10.831 14.0129 10.409 13.591C9.98705 13.169 9.75 12.5967 9.75 12Z"
                                      fill="#155DFC"
                                    />
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M12 3.25C7.486 3.25 4.445 5.954 2.68 8.247L2.649 8.288C2.249 8.807 1.882 9.284 1.633 9.848C1.366 10.453 1.25 11.112 1.25 12C1.25 12.888 1.366 13.547 1.633 14.152C1.883 14.716 2.25 15.194 2.649 15.712L2.681 15.753C4.445 18.046 7.486 20.75 12 20.75C16.514 20.75 19.555 18.046 21.32 15.753L21.351 15.712C21.751 15.194 22.118 14.716 22.367 14.152C22.634 13.547 22.75 12.888 22.75 12C22.75 11.112 22.634 10.453 22.367 9.848C22.117 9.284 21.75 8.807 21.351 8.288L21.319 8.247C19.555 5.954 16.514 3.25 12 3.25ZM3.87 9.162C5.498 7.045 8.15 4.75 12 4.75C15.85 4.75 18.501 7.045 20.13 9.162C20.57 9.732 20.826 10.072 20.995 10.454C21.153 10.812 21.25 11.249 21.25 12C21.25 12.751 21.153 13.188 20.995 13.546C20.826 13.928 20.569 14.268 20.131 14.838C18.5 16.955 15.85 19.25 12 19.25C8.15 19.25 5.499 16.955 3.87 14.838C3.43 14.268 3.174 13.928 3.005 13.546C2.847 13.188 2.75 12.751 2.75 12C2.75 11.249 2.847 10.812 3.005 10.454C3.174 10.072 3.432 9.732 3.87 9.162Z"
                                      fill="#155DFC"
                                    />
                                  </svg>
                                </button>
                              </Link>
                              {PRINTABLE_STATUSES.includes(item.status) && (
                                <button
                                  className={styles["apt-view-btn"]}
                                  title="Print Invoice"
                                  onClick={() => openInvoice(item)}
                                  style={{
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                  }}
                                >
                                  <FiPrinter size={18} color="#111827" />
                                </button>
                              )}

                              <ChatIconWithBadge
                                className={styles["apt-view-btn"]}
                                title="Chat"
                                count={unreadForAppointment(
                                  item.bookingId ?? item.booking_id ?? item.id,
                                )}
                                onClick={() => openChat(item)}
                              />
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Awaiting Payment Tab */}
            {activeTab === "Awaiting Payment" && (
              <div className={styles["apt-appointments-table"]}>
                <table>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>{t("childBooking.childName", "Child Name")}</th>
                      <th>Test Name</th>
                      <th>Service Date</th>
                      <th>Service Time</th>
                      <th>Urgency</th>
                      <th>Location</th>
                      <th>Total Price (IQD)</th>
                      <th>Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={10}>
                          <Loader />
                        </td>
                      </tr>
                    ) : !(imagingOrders || []).length ? (
                      <tr>
                        <td
                          colSpan={10}
                          style={{
                            textAlign: "center",
                            padding: "20px",
                            color: "#888",
                          }}
                        >
                          No records found.
                        </td>
                      </tr>
                    ) : (
                      imagingOrders.map((item, idx) => (
                        <tr key={item.id || idx}>
                          <td>{item.bookingNumber || item.id}</td>
                          {renderPatientCell(item)}
                          {renderChildCell(item)}

                          {/* Test Name with tooltip list */}
                          <td>
                            {(() => {
                              const testNames = (item.tests || [])
                                .map((t) => t.testName)
                                .filter(Boolean);
                              const display = testNames.join(", ") || "-";
                              return testNames.length > 1 ? (
                                <div className={styles["testNames"]}>
                                  <div className={styles["truncateTestNames"]}>
                                    {display}
                                  </div>
                                  <div className={styles["testNamesList"]}>
                                    <ul>
                                      <li>
                                        <span>Tests</span>
                                      </li>
                                      {testNames.map((name, i) => (
                                        <li key={i}>{name}</li>
                                      ))}
                                    </ul>
                                  </div>
                                </div>
                              ) : (
                                display
                              );
                            })()}
                          </td>

                          <td>
                            {item.scheduledDate
                              ? tzDateNumeric(new Date(item.scheduledDate))
                              : "-"}
                          </td>
                          <td>{formatTime12h(item.scheduledTime)}</td>

                          {/* Test Type */}
                          {/* <td>
                            <span
                              className={`${styles["db-status"]} ${
                                styles[getStatusColor(item.testType)]
                              }`}
                            >
                              {item.testType}
                            </span>
                          </td> */}

                          {/* Urgency */}
                          <td>
                            {item.urgency ? (
                              <span
                                className={`${styles["db-status"]} ${
                                  styles[getStatusColor(item.urgency)]
                                }`}
                              >
                                {item.urgency}
                              </span>
                            ) : (
                              ""
                            )}
                          </td>

                          {/* Location */}
                          <td>
                            <span
                              className={`${styles["db-status"]} ${
                                styles[
                                  getStatusColor(
                                    formatLocation(item.preferredLocation),
                                  )
                                ]
                              }`}
                            >
                              {formatLocation(item.preferredLocation)}
                            </span>
                          </td>

                          {/* Total Price */}
                          <td>
                            IQD{" "}
                            {formatWholeAmount(item.invoice?.total ?? 0)}
                          </td>

                          {/* ACTION COLUMN (UNCHANGED) */}
                          <td>
                            <div className={styles["apt-action-buttons"]}>
                              <Link
                                to={`/imaging_appointment_detail/${item.id}`}
                              >
                                <button
                                  className={styles["apt-view-btn"]}
                                  title="View"
                                >
                                  <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M12 8.25C11.0054 8.25 10.0516 8.64509 9.34835 9.34835C8.64509 10.0516 8.25 11.0054 8.25 12C8.25 12.9946 8.64509 13.9484 9.34835 14.6516C10.0516 15.3549 11.0054 15.75 12 15.75C12.9946 15.75 13.9484 15.3549 14.6516 14.6516C15.3549 13.9484 15.75 12.9946 15.75 12C15.75 11.0054 15.3549 10.0516 14.6516 9.34835C13.9484 8.64509 12.9946 8.25 12 8.25ZM9.75 12C9.75 11.4033 9.98705 10.831 10.409 10.409C10.831 9.98705 11.4033 9.75 12 9.75C12.5967 9.75 13.169 9.98705 13.591 10.409C14.0129 10.831 14.25 11.4033 14.25 12C14.25 12.5967 14.0129 13.169 13.591 13.591C13.169 14.0129 12.5967 14.25 12 14.25C11.4033 14.25 10.831 14.0129 10.409 13.591C9.98705 13.169 9.75 12.5967 9.75 12Z"
                                      fill="#155DFC"
                                    />
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M12 3.25C7.486 3.25 4.445 5.954 2.68 8.247L2.649 8.288C2.249 8.807 1.882 9.284 1.633 9.848C1.366 10.453 1.25 11.112 1.25 12C1.25 12.888 1.366 13.547 1.633 14.152C1.883 14.716 2.25 15.194 2.649 15.712L2.681 15.753C4.445 18.046 7.486 20.75 12 20.75C16.514 20.75 19.555 18.046 21.32 15.753L21.351 15.712C21.751 15.194 22.118 14.716 22.367 14.152C22.634 13.547 22.75 12.888 22.75 12C22.75 11.112 22.634 10.453 22.367 9.848C22.117 9.284 21.75 8.807 21.351 8.288L21.319 8.247C19.555 5.954 16.514 3.25 12 3.25ZM3.87 9.162C5.498 7.045 8.15 4.75 12 4.75C15.85 4.75 18.501 7.045 20.13 9.162C20.57 9.732 20.826 10.072 20.995 10.454C21.153 10.812 21.25 11.249 21.25 12C21.25 12.751 21.153 13.188 20.995 13.546C20.826 13.928 20.569 14.268 20.131 14.838C18.5 16.955 15.85 19.25 12 19.25C8.15 19.25 5.499 16.955 3.87 14.838C3.43 14.268 3.174 13.928 3.005 13.546C2.847 13.188 2.75 12.751 2.75 12C2.75 11.249 2.847 10.812 3.005 10.454C3.174 10.072 3.432 9.732 3.87 9.162Z"
                                      fill="#155DFC"
                                    />
                                  </svg>
                                </button>
                              </Link>
                              {PRINTABLE_STATUSES.includes(item.status) && (
                                <button
                                  className={styles["apt-view-btn"]}
                                  title="Print Invoice"
                                  onClick={() => openInvoice(item)}
                                  style={{
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                  }}
                                >
                                  <FiPrinter size={18} color="#111827" />
                                </button>
                              )}

                              <ChatIconWithBadge
                                className={styles["apt-view-btn"]}
                                title="Chat"
                                count={unreadForAppointment(
                                  item.bookingId ?? item.booking_id ?? item.id,
                                )}
                                onClick={() => openChat(item)}
                              />
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Order Accepted tab  */}
            {activeTab === "Order Accepted" && (
              <div className={styles["apt-appointments-table"]}>
                <table>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>{t("childBooking.childName", "Child Name")}</th>
                      <th>Test Name</th>
                      <th>Service Date</th>
                      <th>Service Time</th>
                      {/* <th>Test Type</th> */}
                      <th>Urgency</th>
                      <th>Location</th>
                      <th>Total Price (IQD)</th>
                      <th>Payment Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={11}>
                          <Loader />
                        </td>
                      </tr>
                    ) : !(imagingOrders || []).length ? (
                      <tr>
                        <td
                          colSpan={11}
                          style={{
                            textAlign: "center",
                            padding: "20px",
                            color: "#888",
                          }}
                        >
                          No records found.
                        </td>
                      </tr>
                    ) : (
                      imagingOrders.map((item, idx) => (
                        <tr key={item.id || idx}>
                          <td>{item.bookingNumber || item.id}</td>
                          {renderPatientCell(item)}
                          {renderChildCell(item)}

                          <td>
                            {(() => {
                              const testNames = (item.tests || [])
                                .map((t) => t.testName)
                                .filter(Boolean);
                              const display = testNames.join(", ") || "-";
                              return testNames.length > 1 ? (
                                <div className={styles["testNames"]}>
                                  <div className={styles["truncateTestNames"]}>
                                    {display}
                                  </div>
                                  <div className={styles["testNamesList"]}>
                                    <ul>
                                      <li>
                                        <span>Tests</span>
                                      </li>
                                      {testNames.map((name, i) => (
                                        <li key={i}>{name}</li>
                                      ))}
                                    </ul>
                                  </div>
                                </div>
                              ) : (
                                display
                              );
                            })()}
                          </td>

                          <td>
                            {item.scheduledDate
                              ? tzDateNumeric(new Date(item.scheduledDate))
                              : "-"}
                          </td>
                          <td>{formatTime12h(item.scheduledTime)}</td>

                          {/* <td>
                            <span
                              className={`${styles["db-status"]} ${styles[getStatusColor(item.testType)]}`}
                            >
                              {item.testType}
                            </span>
                          </td> */}

                          <td>
                            {item.urgency ? (
                              <span
                                className={`${styles["db-status"]} ${styles[getStatusColor(item.urgency)]}`}
                              >
                                {item.urgency}
                              </span>
                            ) : (
                              ""
                            )}
                          </td>

                          <td>
                            <span
                              className={`${styles["db-status"]} ${styles[getStatusColor(formatLocation(item.preferredLocation))]}`}
                            >
                              {formatLocation(item.preferredLocation)}
                            </span>
                          </td>

                          <td>
                            IQD{" "}
                            {formatWholeAmount(item.invoice?.total ?? 0)}
                          </td>

                          {/* Payment Status */}
                          <td>
                            <span
                              className={`${styles["db-status"]} ${styles[getStatusColor(formatPaymentStatus(item.paymentStatus))]}`}
                            >
                              {formatPaymentStatus(item.paymentStatus)}
                            </span>
                          </td>

                          {/* ACTION COLUMN (UNCHANGED) */}
                          <td>
                            <div className={styles["apt-action-buttons"]}>
                              <Link
                                to={`/imaging_appointment_detail/${item.id}`}
                              >
                                <button
                                  className={styles["apt-view-btn"]}
                                  title="View"
                                >
                                  <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M12 8.25C11.0054 8.25 10.0516 8.64509 9.34835 9.34835C8.64509 10.0516 8.25 11.0054 8.25 12C8.25 12.9946 8.64509 13.9484 9.34835 14.6516C10.0516 15.3549 11.0054 15.75 12 15.75C12.9946 15.75 13.9484 15.3549 14.6516 14.6516C15.3549 13.9484 15.75 12.9946 15.75 12C15.75 11.0054 15.3549 10.0516 14.6516 9.34835C13.9484 8.64509 12.9946 8.25 12 8.25ZM9.75 12C9.75 11.4033 9.98705 10.831 10.409 10.409C10.831 9.98705 11.4033 9.75 12 9.75C12.5967 9.75 13.169 9.98705 13.591 10.409C14.0129 10.831 14.25 11.4033 14.25 12C14.25 12.5967 14.0129 13.169 13.591 13.591C13.169 14.0129 12.5967 14.25 12 14.25C11.4033 14.25 10.831 14.0129 10.409 13.591C9.98705 13.169 9.75 12.5967 9.75 12Z"
                                      fill="#155DFC"
                                    />
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M12 3.25C7.486 3.25 4.445 5.954 2.68 8.247L2.649 8.288C2.249 8.807 1.882 9.284 1.633 9.848C1.366 10.453 1.25 11.112 1.25 12C1.25 12.888 1.366 13.547 1.633 14.152C1.883 14.716 2.25 15.194 2.649 15.712L2.681 15.753C4.445 18.046 7.486 20.75 12 20.75C16.514 20.75 19.555 18.046 21.32 15.753L21.351 15.712C21.751 15.194 22.118 14.716 22.367 14.152C22.634 13.547 22.75 12.888 22.75 12C22.75 11.112 22.634 10.453 22.367 9.848C22.117 9.284 21.75 8.807 21.351 8.288L21.319 8.247C19.555 5.954 16.514 3.25 12 3.25ZM3.87 9.162C5.498 7.045 8.15 4.75 12 4.75C15.85 4.75 18.501 7.045 20.13 9.162C20.57 9.732 20.826 10.072 20.995 10.454C21.153 10.812 21.25 11.249 21.25 12C21.25 12.751 21.153 13.188 20.995 13.546C20.826 13.928 20.569 14.268 20.131 14.838C18.5 16.955 15.85 19.25 12 19.25C8.15 19.25 5.499 16.955 3.87 14.838C3.43 14.268 3.174 13.928 3.005 13.546C2.847 13.188 2.75 12.751 2.75 12C2.75 11.249 2.847 10.812 3.005 10.454C3.174 10.072 3.432 9.732 3.87 9.162Z"
                                      fill="#155DFC"
                                    />
                                  </svg>
                                </button>
                              </Link>

                              {PRINTABLE_STATUSES.includes(item.status) && (
                                <button
                                  className={styles["apt-view-btn"]}
                                  title="Print Invoice"
                                  onClick={() => openInvoice(item)}
                                  style={{
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                  }}
                                >
                                  <FiPrinter size={18} color="#111827" />
                                </button>
                              )}

                              <ChatIconWithBadge
                                className={styles["apt-view-btn"]}
                                title="Chat"
                                count={unreadForAppointment(
                                  item.bookingId ?? item.booking_id ?? item.id,
                                )}
                                onClick={() => openChat(item)}
                              />
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Scheduled tab  */}
            {(activeTab === "Scheduled" || activeTab === "Dispatched") && (
              <div className={styles["apt-appointments-table"]}>
                <table>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>{t("childBooking.childName", "Child Name")}</th>
                      <th>Test Name</th>
                      <th>Service Date</th>
                      <th>Service Time</th>
                      {/* <th>Test Type</th> */}
                      <th>Urgency</th>
                      <th>Location</th>
                      <th>Total Price (IQD)</th>
                      <th>Payment Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={11}>
                          <Loader />
                        </td>
                      </tr>
                    ) : !(imagingOrders || []).length ? (
                      <tr>
                        <td
                          colSpan={11}
                          style={{
                            textAlign: "center",
                            padding: "20px",
                            color: "#888",
                          }}
                        >
                          No records found.
                        </td>
                      </tr>
                    ) : (
                      imagingOrders.map((item, idx) => (
                        <tr key={item.id || idx}>
                          <td>{item.bookingNumber || item.id}</td>
                          {renderPatientCell(item)}
                          {renderChildCell(item)}

                          <td>
                            {(() => {
                              const testNames = (item.tests || [])
                                .map((t) => t.testName)
                                .filter(Boolean);
                              const display = testNames.join(", ") || "-";
                              return testNames.length > 1 ? (
                                <div className={styles["testNames"]}>
                                  <div className={styles["truncateTestNames"]}>
                                    {display}
                                  </div>
                                  <div className={styles["testNamesList"]}>
                                    <ul>
                                      <li>
                                        <span>Tests</span>
                                      </li>
                                      {testNames.map((name, i) => (
                                        <li key={i}>{name}</li>
                                      ))}
                                    </ul>
                                  </div>
                                </div>
                              ) : (
                                display
                              );
                            })()}
                          </td>

                          <td>
                            {item.scheduledDate
                              ? tzDateNumeric(new Date(item.scheduledDate))
                              : "-"}
                          </td>
                          <td>{formatTime12h(item.scheduledTime)}</td>

                          {/* <td>
                            <span
                              className={`${styles["db-status"]} ${styles[getStatusColor(item.testType)]}`}
                            >
                              {item.testType}
                            </span>
                          </td> */}

                          <td>
                            {item.urgency ? (
                              <span
                                className={`${styles["db-status"]} ${styles[getStatusColor(item.urgency)]}`}
                              >
                                {item.urgency}
                              </span>
                            ) : (
                              ""
                            )}
                          </td>

                          <td>
                            <span
                              className={`${styles["db-status"]} ${styles[getStatusColor(formatLocation(item.preferredLocation))]}`}
                            >
                              {formatLocation(item.preferredLocation)}
                            </span>
                          </td>

                          <td>
                            IQD{" "}
                            {formatWholeAmount(item.invoice?.total ?? 0)}
                          </td>

                          {/* Payment Status */}
                          <td>
                            <span
                              className={`${styles["db-status"]} ${styles[getStatusColor(formatPaymentStatus(item.paymentStatus))]}`}
                            >
                              {formatPaymentStatus(item.paymentStatus)}
                            </span>
                          </td>

                          {/* ACTION COLUMN (UNCHANGED) */}
                          <td>
                            <div className={styles["apt-action-buttons"]}>
                              <Link
                                to={`/imaging_appointment_detail/${item.id}`}
                              >
                                <button
                                  className={styles["apt-view-btn"]}
                                  title="View"
                                >
                                  <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M12 8.25C11.0054 8.25 10.0516 8.64509 9.34835 9.34835C8.64509 10.0516 8.25 11.0054 8.25 12C8.25 12.9946 8.64509 13.9484 9.34835 14.6516C10.0516 15.3549 11.0054 15.75 12 15.75C12.9946 15.75 13.9484 15.3549 14.6516 14.6516C15.3549 13.9484 15.75 12.9946 15.75 12C15.75 11.0054 15.3549 10.0516 14.6516 9.34835C13.9484 8.64509 12.9946 8.25 12 8.25ZM9.75 12C9.75 11.4033 9.98705 10.831 10.409 10.409C10.831 9.98705 11.4033 9.75 12 9.75C12.5967 9.75 13.169 9.98705 13.591 10.409C14.0129 10.831 14.25 11.4033 14.25 12C14.25 12.5967 14.0129 13.169 13.591 13.591C13.169 14.0129 12.5967 14.25 12 14.25C11.4033 14.25 10.831 14.0129 10.409 13.591C9.98705 13.169 9.75 12.5967 9.75 12Z"
                                      fill="#155DFC"
                                    />
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M12 3.25C7.486 3.25 4.445 5.954 2.68 8.247L2.649 8.288C2.249 8.807 1.882 9.284 1.633 9.848C1.366 10.453 1.25 11.112 1.25 12C1.25 12.888 1.366 13.547 1.633 14.152C1.883 14.716 2.25 15.194 2.649 15.712L2.681 15.753C4.445 18.046 7.486 20.75 12 20.75C16.514 20.75 19.555 18.046 21.32 15.753L21.351 15.712C21.751 15.194 22.118 14.716 22.367 14.152C22.634 13.547 22.75 12.888 22.75 12C22.75 11.112 22.634 10.453 22.367 9.848C22.117 9.284 21.75 8.807 21.351 8.288L21.319 8.247C19.555 5.954 16.514 3.25 12 3.25ZM3.87 9.162C5.498 7.045 8.15 4.75 12 4.75C15.85 4.75 18.501 7.045 20.13 9.162C20.57 9.732 20.826 10.072 20.995 10.454C21.153 10.812 21.25 11.249 21.25 12C21.25 12.751 21.153 13.188 20.995 13.546C20.826 13.928 20.569 14.268 20.131 14.838C18.5 16.955 15.85 19.25 12 19.25C8.15 19.25 5.499 16.955 3.87 14.838C3.43 14.268 3.174 13.928 3.005 13.546C2.847 13.188 2.75 12.751 2.75 12C2.75 11.249 2.847 10.812 3.005 10.454C3.174 10.072 3.432 9.732 3.87 9.162Z"
                                      fill="#155DFC"
                                    />
                                  </svg>
                                </button>
                              </Link>

                              {PRINTABLE_STATUSES.includes(item.status) && (
                                <button
                                  className={styles["apt-view-btn"]}
                                  title="Print Invoice"
                                  onClick={() => openInvoice(item)}
                                  style={{
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                  }}
                                >
                                  <FiPrinter size={18} color="#111827" />
                                </button>
                              )}

                              <ChatIconWithBadge
                                className={styles["apt-view-btn"]}
                                title="Chat"
                                count={unreadForAppointment(
                                  item.bookingId ?? item.booking_id ?? item.id,
                                )}
                                onClick={() => openChat(item)}
                              />
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* In-Progress tab  */}
            {activeTab === "In-Progress" && (
              <div className={styles["apt-appointments-table"]}>
                <table>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>{t("childBooking.childName", "Child Name")}</th>
                      <th>Test Name</th>
                      <th>Service Date</th>
                      <th>Service Time</th>
                      <th>Urgency</th>
                      <th>Location</th>
                      <th>Payment Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={10}>
                          <Loader />
                        </td>
                      </tr>
                    ) : !(imagingOrders || []).length ? (
                      <tr>
                        <td
                          colSpan={10}
                          style={{
                            textAlign: "center",
                            padding: "20px",
                            color: "#888",
                          }}
                        >
                          No records found.
                        </td>
                      </tr>
                    ) : (
                      imagingOrders.map((item, idx) => (
                        <tr key={item.id || idx}>
                          <td>{item.bookingNumber || item.id}</td>
                          {renderPatientCell(item)}
                          {renderChildCell(item)}

                          <td>
                            {(() => {
                              const testNames = (item.tests || [])
                                .map((t) => t.testName)
                                .filter(Boolean);
                              const display = testNames.join(", ") || "-";
                              return testNames.length > 1 ? (
                                <div className={styles["testNames"]}>
                                  <div className={styles["truncateTestNames"]}>
                                    {display}
                                  </div>
                                  <div className={styles["testNamesList"]}>
                                    <ul>
                                      <li>
                                        <span>Tests</span>
                                      </li>
                                      {testNames.map((name, i) => (
                                        <li key={i}>{name}</li>
                                      ))}
                                    </ul>
                                  </div>
                                </div>
                              ) : (
                                display
                              );
                            })()}
                          </td>

                          <td>
                            {item.scheduledDate
                              ? tzDateNumeric(new Date(item.scheduledDate))
                              : "-"}
                          </td>
                          <td>{formatTime12h(item.scheduledTime)}</td>

                          {/* <td>
                            <span
                              className={`${styles["db-status"]} ${styles[getStatusColor(item.testType)]}`}
                            >
                              {item.testType}
                            </span>
                          </td> */}

                          <td>
                            {item.urgency ? (
                              <span
                                className={`${styles["db-status"]} ${styles[getStatusColor(item.urgency)]}`}
                              >
                                {item.urgency}
                              </span>
                            ) : (
                              ""
                            )}
                          </td>

                          <td>
                            <span
                              className={`${styles["db-status"]} ${styles[getStatusColor(formatLocation(item.preferredLocation))]}`}
                            >
                              {formatLocation(item.preferredLocation)}
                            </span>
                          </td>

                          {/* Payment Status */}
                          <td>
                            <span
                              className={`${styles["db-status"]} ${styles[getStatusColor(formatPaymentStatus(item.paymentStatus))]}`}
                            >
                              {formatPaymentStatus(item.paymentStatus)}
                            </span>
                          </td>

                          {/* ACTION COLUMN (UNCHANGED) */}
                          <td>
                            <div className={styles["apt-action-buttons"]}>
                              <Link
                                to={`/imaging_appointment_detail/${item.id}`}
                              >
                                <button
                                  className={styles["apt-view-btn"]}
                                  title="View"
                                >
                                  <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M12 8.25C11.0054 8.25 10.0516 8.64509 9.34835 9.34835C8.64509 10.0516 8.25 11.0054 8.25 12C8.25 12.9946 8.64509 13.9484 9.34835 14.6516C10.0516 15.3549 11.0054 15.75 12 15.75C12.9946 15.75 13.9484 15.3549 14.6516 14.6516C15.3549 13.9484 15.75 12.9946 15.75 12C15.75 11.0054 15.3549 10.0516 14.6516 9.34835C13.9484 8.64509 12.9946 8.25 12 8.25ZM9.75 12C9.75 11.4033 9.98705 10.831 10.409 10.409C10.831 9.98705 11.4033 9.75 12 9.75C12.5967 9.75 13.169 9.98705 13.591 10.409C14.0129 10.831 14.25 11.4033 14.25 12C14.25 12.5967 14.0129 13.169 13.591 13.591C13.169 14.0129 12.5967 14.25 12 14.25C11.4033 14.25 10.831 14.0129 10.409 13.591C9.98705 13.169 9.75 12.5967 9.75 12Z"
                                      fill="#155DFC"
                                    />
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M12 3.25C7.486 3.25 4.445 5.954 2.68 8.247L2.649 8.288C2.249 8.807 1.882 9.284 1.633 9.848C1.366 10.453 1.25 11.112 1.25 12C1.25 12.888 1.366 13.547 1.633 14.152C1.883 14.716 2.25 15.194 2.649 15.712L2.681 15.753C4.445 18.046 7.486 20.75 12 20.75C16.514 20.75 19.555 18.046 21.32 15.753L21.351 15.712C21.751 15.194 22.118 14.716 22.367 14.152C22.634 13.547 22.75 12.888 22.75 12C22.75 11.112 22.634 10.453 22.367 9.848C22.117 9.284 21.75 8.807 21.351 8.288L21.319 8.247C19.555 5.954 16.514 3.25 12 3.25ZM3.87 9.162C5.498 7.045 8.15 4.75 12 4.75C15.85 4.75 18.501 7.045 20.13 9.162C20.57 9.732 20.826 10.072 20.995 10.454C21.153 10.812 21.25 11.249 21.25 12C21.25 12.751 21.153 13.188 20.995 13.546C20.826 13.928 20.569 14.268 20.131 14.838C18.5 16.955 15.85 19.25 12 19.25C8.15 19.25 5.499 16.955 3.87 14.838C3.43 14.268 3.174 13.928 3.005 13.546C2.847 13.188 2.75 12.751 2.75 12C2.75 11.249 2.847 10.812 3.005 10.454C3.174 10.072 3.432 9.732 3.87 9.162Z"
                                      fill="#155DFC"
                                    />
                                  </svg>
                                </button>
                              </Link>

                              {PRINTABLE_STATUSES.includes(item.status) && (
                                <button
                                  className={styles["apt-view-btn"]}
                                  title="Print Invoice"
                                  onClick={() => openInvoice(item)}
                                  style={{
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                  }}
                                >
                                  <FiPrinter size={18} color="#111827" />
                                </button>
                              )}

                              <ChatIconWithBadge
                                className={styles["apt-view-btn"]}
                                title="Chat"
                                count={unreadForAppointment(
                                  item.bookingId ?? item.booking_id ?? item.id,
                                )}
                                onClick={() => openChat(item)}
                              />
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Completed Tab (also handles the consolidated "Results /
                Pending Results" tab — both are COMPLETED bookings server-
                side, the latter also gated on `payment_status = 'COMPLETED'`
                via the RESULTS_PENDING_OR_SENT pseudo-status in
                `getImagingOrders`). Reusing this single render block instead
                of duplicating the table. */}
            {(activeTab === "Completed" ||
              activeTab === "Results / Pending Results") && (
              <div className={styles["apt-appointments-table"]}>
                <table>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>{t("childBooking.childName", "Child Name")}</th>
                      <th>Test Name</th>
                      <th>Service Date</th>
                      <th>Service Time</th>
                      {/* <th>Test Type</th> */}
                      <th>Urgency</th>
                      <th>Location</th>
                      <th>Total Price (IQD)</th>
                      <th>Payment Status</th>
                      <th>Report Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={12}>
                          <Loader />
                        </td>
                      </tr>
                    ) : !(imagingOrders || []).length ? (
                      <tr>
                        <td
                          colSpan={12}
                          style={{
                            textAlign: "center",
                            padding: "20px",
                            color: "#888",
                          }}
                        >
                          No records found.
                        </td>
                      </tr>
                    ) : (
                      imagingOrders.map((item, idx) => (
                        <tr key={item.id || idx}>
                          <td>{item.bookingNumber || item.id}</td>
                          {renderPatientCell(item)}
                          {renderChildCell(item)}

                          <td>
                            {(() => {
                              const testNames = (item.tests || [])
                                .map((t) => t.testName)
                                .filter(Boolean);
                              const display = testNames.join(", ") || "-";
                              return testNames.length > 1 ? (
                                <div className={styles["testNames"]}>
                                  <div className={styles["truncateTestNames"]}>
                                    {display}
                                  </div>
                                  <div className={styles["testNamesList"]}>
                                    <ul>
                                      <li>
                                        <span>Tests</span>
                                      </li>
                                      {testNames.map((name, i) => (
                                        <li key={i}>{name}</li>
                                      ))}
                                    </ul>
                                  </div>
                                </div>
                              ) : (
                                display
                              );
                            })()}
                          </td>

                          <td>
                            {item.scheduledDate
                              ? tzDateNumeric(new Date(item.scheduledDate))
                              : "-"}
                          </td>
                          <td>{formatTime12h(item.scheduledTime)}</td>

                          {/* <td>
                            <span
                              className={`${styles["db-status"]} ${styles[getStatusColor(item.testType)]}`}
                            >
                              {item.testType}
                            </span>
                          </td> */}

                          <td>
                            {item.urgency ? (
                              <span
                                className={`${styles["db-status"]} ${styles[getStatusColor(item.urgency)]}`}
                              >
                                {item.urgency}
                              </span>
                            ) : (
                              ""
                            )}
                          </td>

                          <td>
                            <span
                              className={`${styles["db-status"]} ${styles[getStatusColor(formatLocation(item.preferredLocation))]}`}
                            >
                              {formatLocation(item.preferredLocation)}
                            </span>
                          </td>

                          <td>
                            IQD{" "}
                            {formatWholeAmount(item.invoice?.total ?? 0)}
                          </td>

                          {/* Payment Status */}
                          <td>
                            <span
                              className={`${styles["db-status"]} ${styles[getStatusColor(formatPaymentStatus(item.paymentStatus))]}`}
                            >
                              {formatPaymentStatus(item.paymentStatus)}
                            </span>
                          </td>

                          {/* Report Status */}
                          <td>
                            <span
                              className={`${styles["db-status"]} ${styles[getReportStatusColor(item.reportStatus)]}`}
                            >
                              {formatReportStatus(item.reportStatus)}
                            </span>
                          </td>

                          {/* ACTION COLUMN (UNCHANGED) */}
                          <td>
                            <div className={styles["apt-action-buttons"]}>
                              <Link
                                to={`/imaging_appointment_detail/${item.id}`}
                              >
                                <button
                                  className={styles["apt-view-btn"]}
                                  title="View"
                                >
                                  <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M12 8.25C11.0054 8.25 10.0516 8.64509 9.34835 9.34835C8.64509 10.0516 8.25 11.0054 8.25 12C8.25 12.9946 8.64509 13.9484 9.34835 14.6516C10.0516 15.3549 11.0054 15.75 12 15.75C12.9946 15.75 13.9484 15.3549 14.6516 14.6516C15.3549 13.9484 15.75 12.9946 15.75 12C15.75 11.0054 15.3549 10.0516 14.6516 9.34835C13.9484 8.64509 12.9946 8.25 12 8.25ZM9.75 12C9.75 11.4033 9.98705 10.831 10.409 10.409C10.831 9.98705 11.4033 9.75 12 9.75C12.5967 9.75 13.169 9.98705 13.591 10.409C14.0129 10.831 14.25 11.4033 14.25 12C14.25 12.5967 14.0129 13.169 13.591 13.591C13.169 14.0129 12.5967 14.25 12 14.25C11.4033 14.25 10.831 14.0129 10.409 13.591C9.98705 13.169 9.75 12.5967 9.75 12Z"
                                      fill="#155DFC"
                                    />
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M12 3.25C7.486 3.25 4.445 5.954 2.68 8.247L2.649 8.288C2.249 8.807 1.882 9.284 1.633 9.848C1.366 10.453 1.25 11.112 1.25 12C1.25 12.888 1.366 13.547 1.633 14.152C1.883 14.716 2.25 15.194 2.649 15.712L2.681 15.753C4.445 18.046 7.486 20.75 12 20.75C16.514 20.75 19.555 18.046 21.32 15.753L21.351 15.712C21.751 15.194 22.118 14.716 22.367 14.152C22.634 13.547 22.75 12.888 22.75 12C22.75 11.112 22.634 10.453 22.367 9.848C22.117 9.284 21.75 8.807 21.351 8.288L21.319 8.247C19.555 5.954 16.514 3.25 12 3.25ZM3.87 9.162C5.498 7.045 8.15 4.75 12 4.75C15.85 4.75 18.501 7.045 20.13 9.162C20.57 9.732 20.826 10.072 20.995 10.454C21.153 10.812 21.25 11.249 21.25 12C21.25 12.751 21.153 13.188 20.995 13.546C20.826 13.928 20.569 14.268 20.131 14.838C18.5 16.955 15.85 19.25 12 19.25C8.15 19.25 5.499 16.955 3.87 14.838C3.43 14.268 3.174 13.928 3.005 13.546C2.847 13.188 2.75 12.751 2.75 12C2.75 11.249 2.847 10.812 3.005 10.454C3.174 10.072 3.432 9.732 3.87 9.162Z"
                                      fill="#155DFC"
                                    />
                                  </svg>
                                </button>
                              </Link>

                              {PRINTABLE_STATUSES.includes(item.status) && (
                                <button
                                  className={styles["apt-view-btn"]}
                                  title="Print Invoice"
                                  onClick={() => openInvoice(item)}
                                  style={{
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                  }}
                                >
                                  <FiPrinter size={18} color="#111827" />
                                </button>
                              )}

                              <ChatIconWithBadge
                                className={styles["apt-view-btn"]}
                                title="Chat"
                                count={unreadForAppointment(
                                  item.bookingId ?? item.booking_id ?? item.id,
                                )}
                                onClick={() => openChat(item)}
                              />
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Rejected Tab */}
            {activeTab === "Rejected" && (
              <div className={styles["apt-appointments-table"]}>
                <table>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>{t("childBooking.childName", "Child Name")}</th>
                      <th>Test Name</th>
                      <th>Request Date</th>
                      <th>Preferred Date</th>
                      <th>Preferred Time Slot</th>
                      {/* <th>Test Type</th> */}
                      <th>Urgency</th>
                      <th>Preferred Technician Gender</th>
                      <th>Preferred Location</th>
                      <th>Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={11}>
                          <Loader />
                        </td>
                      </tr>
                    ) : !(imagingOrders || []).length ? (
                      <tr>
                        <td
                          colSpan={11}
                          style={{
                            textAlign: "center",
                            padding: "20px",
                            color: "#888",
                          }}
                        >
                          No records found.
                        </td>
                      </tr>
                    ) : (
                      imagingOrders.map((item, idx) => (
                        <tr key={item.id || idx}>
                          <td>{item.bookingNumber || item.id}</td>
                          {renderPatientCell(item)}
                          {renderChildCell(item)}

                          <td>
                            {(() => {
                              const testNames = (item.tests || [])
                                .map((t) => t.testName)
                                .filter(Boolean);
                              const display = testNames.join(", ") || "-";
                              return testNames.length > 1 ? (
                                <div className={styles["testNames"]}>
                                  <div className={styles["truncateTestNames"]}>
                                    {display}
                                  </div>
                                  <div className={styles["testNamesList"]}>
                                    <ul>
                                      <li>
                                        <span>Tests</span>
                                      </li>
                                      {testNames.map((name, i) => (
                                        <li key={i}>{name}</li>
                                      ))}
                                    </ul>
                                  </div>
                                </div>
                              ) : (
                                display
                              );
                            })()}
                          </td>

                          <td>{item.requestDate || "-"}</td>
                          <td>
                            {item.preferredDate
                              ? tzDateNumeric(new Date(item.preferredDate))
                              : "-"}
                          </td>
                          <td>{item.preferredTimeSlot || "-"}</td>

                          {/* <td>
                            <span
                              className={`${styles["db-status"]} ${styles[getStatusColor(item.testType)]}`}
                            >
                              {item.testType}
                            </span>
                          </td> */}

                          <td>
                            {item.urgency ? (
                              <span
                                className={`${styles["db-status"]} ${styles[getStatusColor(item.urgency)]}`}
                              >
                                {item.urgency}
                              </span>
                            ) : (
                              ""
                            )}
                          </td>

                          <td>
                            {item.preferredTechnicianGender ||
                              item.preferredGender ||
                              "-"}
                          </td>
                          <td>
                            <span
                              className={`${styles["db-status"]} ${styles[getStatusColor(formatLocation(item.preferredLocation))]}`}
                            >
                              {formatLocation(item.preferredLocation)}
                            </span>
                          </td>

                          {/* ACTION COLUMN (UNCHANGED) */}
                          <td>
                            <div className={styles["apt-action-buttons"]}>
                              <Link
                                to={`/imaging_appointment_detail/${item.id}`}
                              >
                                <button
                                  className={styles["apt-view-btn"]}
                                  title="View"
                                >
                                  <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M12 8.25C11.0054 8.25 10.0516 8.64509 9.34835 9.34835C8.64509 10.0516 8.25 11.0054 8.25 12C8.25 12.9946 8.64509 13.9484 9.34835 14.6516C10.0516 15.3549 11.0054 15.75 12 15.75C12.9946 15.75 13.9484 15.3549 14.6516 14.6516C15.3549 13.9484 15.75 12.9946 15.75 12C15.75 11.0054 15.3549 10.0516 14.6516 9.34835C13.9484 8.64509 12.9946 8.25 12 8.25ZM9.75 12C9.75 11.4033 9.98705 10.831 10.409 10.409C10.831 9.98705 11.4033 9.75 12 9.75C12.5967 9.75 13.169 9.98705 13.591 10.409C14.0129 10.831 14.25 11.4033 14.25 12C14.25 12.5967 14.0129 13.169 13.591 13.591C13.169 14.0129 12.5967 14.25 12 14.25C11.4033 14.25 10.831 14.0129 10.409 13.591C9.98705 13.169 9.75 12.5967 9.75 12Z"
                                      fill="#155DFC"
                                    />
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M12 3.25C7.486 3.25 4.445 5.954 2.68 8.247L2.649 8.288C2.249 8.807 1.882 9.284 1.633 9.848C1.366 10.453 1.25 11.112 1.25 12C1.25 12.888 1.366 13.547 1.633 14.152C1.883 14.716 2.25 15.194 2.649 15.712L2.681 15.753C4.445 18.046 7.486 20.75 12 20.75C16.514 20.75 19.555 18.046 21.32 15.753L21.351 15.712C21.751 15.194 22.118 14.716 22.367 14.152C22.634 13.547 22.75 12.888 22.75 12C22.75 11.112 22.634 10.453 22.367 9.848C22.117 9.284 21.75 8.807 21.351 8.288L21.319 8.247C19.555 5.954 16.514 3.25 12 3.25ZM3.87 9.162C5.498 7.045 8.15 4.75 12 4.75C15.85 4.75 18.501 7.045 20.13 9.162C20.57 9.732 20.826 10.072 20.995 10.454C21.153 10.812 21.25 11.249 21.25 12C21.25 12.751 21.153 13.188 20.995 13.546C20.826 13.928 20.569 14.268 20.131 14.838C18.5 16.955 15.85 19.25 12 19.25C8.15 19.25 5.499 16.955 3.87 14.838C3.43 14.268 3.174 13.928 3.005 13.546C2.847 13.188 2.75 12.751 2.75 12C2.75 11.249 2.847 10.812 3.005 10.454C3.174 10.072 3.432 9.732 3.87 9.162Z"
                                      fill="#155DFC"
                                    />
                                  </svg>
                                </button>
                              </Link>
                              {/* <button
                                className={styles["apt-view-btn"]}
                                title="View"
                              >
                                <svg
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M12 2.75098C9.54676 2.75098 7.19398 3.72553 5.45927 5.46024C3.72456 7.19495 2.75001 9.54772 2.75001 12.001C2.75001 13.482 3.09801 14.88 3.71501 16.119C3.96301 16.617 4.05801 17.211 3.90201 17.796L3.30601 20.021C3.28099 20.1142 3.28093 20.2124 3.30586 20.3056C3.33078 20.3988 3.37981 20.4839 3.44801 20.5522C3.51621 20.6205 3.60118 20.6696 3.69438 20.6947C3.78759 20.7198 3.88576 20.7199 3.97901 20.695L6.20601 20.099C6.76878 19.9547 7.36489 20.0212 7.88201 20.286C9.16111 20.9231 10.571 21.2535 12 21.251C14.4533 21.251 16.806 20.2764 18.5407 18.5417C20.2755 16.807 21.25 14.4542 21.25 12.001C21.25 9.54772 20.2755 7.19495 18.5407 5.46024C16.806 3.72553 14.4533 2.75098 12 2.75098ZM1.25001 12.001C1.25001 6.06398 6.06301 1.25098 12 1.25098C17.937 1.25098 22.75 6.06398 22.75 12.001C22.75 17.938 17.937 22.751 12 22.751C10.282 22.751 8.65601 22.347 7.21301 21.629C7.02254 21.5291 6.8026 21.5007 6.59301 21.549L4.36701 22.144C2.84301 22.552 1.44901 21.158 1.85701 19.634L2.45301 17.408C2.50128 17.1984 2.4729 16.9784 2.37301 16.788C1.63215 15.3011 1.24767 13.6622 1.25001 12.001ZM7.25001 10.501C7.25001 10.3021 7.32903 10.1113 7.46968 9.97065C7.61033 9.82999 7.8011 9.75098 8.00001 9.75098H16C16.1989 9.75098 16.3897 9.82999 16.5303 9.97065C16.671 10.1113 16.75 10.3021 16.75 10.501C16.75 10.6999 16.671 10.8907 16.5303 11.0313C16.3897 11.172 16.1989 11.251 16 11.251H8.00001C7.8011 11.251 7.61033 11.172 7.46968 11.0313C7.32903 10.8907 7.25001 10.6999 7.25001 10.501ZM7.25001 14.001C7.25001 13.8021 7.32903 13.6113 7.46968 13.4706C7.61033 13.33 7.8011 13.251 8.00001 13.251H13.5C13.6989 13.251 13.8897 13.33 14.0303 13.4706C14.171 13.6113 14.25 13.8021 14.25 14.001C14.25 14.1999 14.171 14.3907 14.0303 14.5313C13.8897 14.672 13.6989 14.751 13.5 14.751H8.00001C7.8011 14.751 7.61033 14.672 7.46968 14.5313C7.32903 14.3907 7.25001 14.1999 7.25001 14.001Z"
                                    fill="#626276"
                                  />
                                </svg>
                              </button> */}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            <div className={styles["apt-pagination"]}>
              <div className={styles["apt-pagination-info"]}>
                {t("dashboard.pagination.showing")}{" "}
                {(currentPage - 1) * itemsPerPage + 1}{" "}
                {t("dashboard.pagination.to")}{" "}
                {(currentPage - 1) * itemsPerPage + imagingOrders.length}{" "}
                {t("dashboard.pagination.of")} {totalPages * itemsPerPage}
              </div>
              <div className={styles["apt-pagination-controls"]}>
                <button
                  className={`${styles["apt-pagination-btn"]} ${
                    currentPage === 1 ? styles["disabled"] : ""
                  }`}
                  // onClick={() => handlePageChange(currentPage - 1)}
                  // onClick={() => handlePageChange11(currentPage)}
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  {t("dashboard.pagination.previous")}
                </button>
                <div className={styles["apt-page-numbers"]}>
                  {/* {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const page = i + 1;
                    return (
                      <button
                        key={page}
                        className={`${styles["apt-page-btn"]} ${
                          styles[`${currentPage === page ? "active" : ""}`]
                        }`}
                        onClick={() => handlePageChange(currentPage)}
                      >
                        {page}
                      </button>
                    );
                  })} */}

                  {Array.from({ length: totalPages }, (_, i) => {
                    const page = i + 1;
                    return (
                      <button
                        key={page}
                        className={`${styles["apt-page-btn"]} ${
                          styles[`${currentPage === page ? "active" : ""}`]
                        }`}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </button>
                    );
                  })}
                </div>
                <button
                  className={`${styles["apt-pagination-btn"]} ${
                    styles["apt-next-btn"]
                  } ${currentPage === totalPages ? styles["disabled"] : ""}`}
                  // onClick={() => handlePageChange(currentPage + 1)}
                  // onClick={() => handlePageChange1(currentPage)}
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  {t("dashboard.pagination.next")}
                </button>
              </div>
            </div>
          </div>{" "}
        </>
      )}

      {isChatOpen && (
        <Chat
          onClose={closeChat}
          patientUserId={
            chatTarget?.patientId ||
            chatTarget?.patient?.user_id ||
            chatTarget?.patient_id ||
            chatTarget?.patient?.id
          }
          bookingId={
            chatTarget?.bookingId || chatTarget?.booking_id || chatTarget?.id
          }
          title={chatTarget ? getPatientName(chatTarget) : "Chat"}
          subtitle={
            chatTarget?.bookingNumber || chatTarget?.booking_number || ""
          }
        />
      )}

      {/* Shared invoice modal — identical layout + fee math everywhere. */}
      {showInvoiceModal && invoiceOrder && (
        <ImagingInvoiceModal
          open={showInvoiceModal}
          onClose={() => setShowInvoiceModal(false)}
          order={invoiceOrder}
          centerProfile={centerProfile}
        />
      )}
    </LabDashboardLayout>
  );
};

export default ImagingOrderManagement;
